import type {
  CellClickedEvent,
  GridApi,
  MenuItemDef,
  GridReadyEvent,
  IRowNode,
  RowSelectedEvent,
} from '@ag-grid-community/core';
import CompanionList from 'src/components/CompanionList/CompanionList';
import { CompanionListProps } from 'src/components/CompanionList/CompanionList.types';
import { CompanionView } from 'configuration-schemas/viewdefns/general';
import { ListViewable } from 'src/common-ui/components/CompanionListView/CompanionListView';
import type { CompanionDataLookup } from 'src/utils/Component/ListView';
import {
  mapValues,
  findIndex,
  isNil,
  parseInt,
  isEmpty,
  isEqual,
  omitBy,
  isBoolean,
  noop,
  concat,
  debounce,
  set,
  intersection,
  uniq,
  uniqBy,
  get,
  cloneDeep,
  head,
  union,
  isArray,
  find,
} from 'lodash';
import React from 'react';

import { Overlay } from 'src/common-ui/index';
import {
  extraRowContainerStyles,
  listPairStyle,
  gridContainerStyle,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.styles';
import {
  ConfigurableGridState,
  ConfigurableGridProps,
  MassColumnUpdateParams,
  ConfigurableGridColumnDef,
  PendingRedecorateActionPayload,
} from 'src/components/ConfigurableGrid/ConfigurableGrid.types';
import { StyledAccordion } from 'src/components/StyledAccordion/StyledAccordion';
import Subheader from 'src/components/Subheader/Subheader.container';
import { SubheaderOwnProps, SubheaderActionButtonProps } from 'src/components/Subheader/Subheader.types';
import ServiceContainer from 'src/ServiceContainer';
import { simpleByField } from 'src/utils/Pivot/Sort';
import { toast } from 'react-toastify';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { BasicItem as PivotBasicItem } from 'src/worker/pivotWorker.types';
import { logError, logWarn } from 'src/services/loggingService';
import { FabType } from '../higherOrder/withFab';
import { CoarseEditPayload } from 'src/dao/pivotClient';
import SplitButton from 'src/common-ui/components/SplitButton/SplitButton';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import { makeValidValuesCache } from 'src/services/validValuesCache';
import { getWorklistMenuItems } from '../WorklistContextMenu/WorklistContextMenu';
import { iconClassToString } from '../WorklistContextMenu/WorklistContextMenu';
import {
  ConfigurableGridButton,
  ConfigurableGridLinkButton,
  TopAttribute,
} from 'configuration-schemas/viewdefns/general';
import ConfigureModal, { ConfigureModalProps, Option } from '../Configure/ConfigureModal';
import { EditableGrid, EditableGridProps, SELECTION_CHECKBOX_COL_ID } from './EditableGrid/EditableGrid';
import { SubheaderDropdownProps } from '../Subheader/SubheaderDropdown';
import { formatConfigurableActionItem } from './utils/ConfigurableGrid.utils';
import MetricLineGraph from 'src/components/Visualize/MetricLineGraph/MetricLineGraph';
import { ActionModal, isListDataButton, isNoSelectButtontype } from './utils/ActionModal';
import { IS_PUBLISHED, STYLE_ID, STYLE_COLOR_ID } from 'src/utils/Domain/Constants';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { isViewDefnLoaded } from 'src/dao/tenantConfigClient';
import { TopAttributes } from 'src/components/ConfigurableGrid/Macros/TopAttributes';
import { generateTopAttributesProps } from 'src/components/ConfigurableGrid/Macros/TopAttributes.utils';
import { Rollup, generateRollupText } from 'src/components/ConfigurableGrid/Macros/Rollup';
import macroStyles from 'src/components/ConfigurableGrid/Macros/Macros.styles';
import { zRefreshOn } from 'configuration-schemas/viewdefns/viewdefn';
import { ViewDataState } from 'src/types/Domain';
import { Skeleton } from '@material-ui/lab';
import type { ViewConfiguratorModalProps } from '../ViewConfiguratorModal/ViewConfiguratorModal.types';
import { AddBreadcrumbPayload } from 'src/state/breadcrumbs/Bread.slice';
import { processApiParams } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';

import { StyledConfigValue, getCompanionClasses, getCompanionDataIndex } from '../ListGridPair/ListGridPair.utils';
import { LinkConfirmationModal } from '../LinkConfirmationModal/LinkConfirmationModal';
import { flattenConfigItems } from 'src/utils/Pivot/Flatten';

export class ConfigurableGrid extends React.Component<ConfigurableGridProps, ConfigurableGridState> {
  gridApi: GridApi | null = null;
  allowEnterList: (string | undefined)[] = [];
  isUnmounting = false;
  /** Get & set column grouped state */
  onColumnGroupOpened = () => {
    if (this.gridApi) {
      const columnGroupState = this.gridApi.getColumnGroupState();
      this.setState({ columnGroupState });
    }
  };

  constructor(props: ConfigurableGridProps) {
    super(props);

    this.state = {
      gridRdyCnt: 0,
      activeStyleColor: '',
      isConfirmationModalOpen: false,
      isConfirmationModalLoading: false,
      actionType: undefined,
      validValuesCache: makeValidValuesCache(),
      isMfpScopeLoading: false,
      isLinkModalOpen: false,
      isTopAttributesRefreshing: false,
    };
  }

  componentDidUpdate(prevProps: ConfigurableGridProps) {
    if (!isEqual(prevProps.data, this.props.data)) {
      this.state.validValuesCache.clear();
    }

    if (!isEqual(prevProps.favoritesList, this.props.favoritesList)) {
      const activeFavorite = this.props.favoritesList.find((x) => x.active === true);
      if (activeFavorite && activeFavorite.jsonBlob) {
        if (activeFavorite.jsonBlob.companionData) {
          const compData = activeFavorite.jsonBlob.companionData;
          this.props.updateCompanionSortField(compData.companionSortField ?? '');
          this.props.updateCompanionSortDirection(compData.companionSortDirection);
        }
        if (activeFavorite.jsonBlob.groupBySelection && this.props.groupByDropdownProps) {
          const nextSelectedIndex = activeFavorite.jsonBlob.groupBySelection || 0;
          const selectedIndex = this.props.groupByDropdownProps?.selection;
          if (nextSelectedIndex !== selectedIndex) {
            this.props.updateGroupBy(nextSelectedIndex);
          }
        }
        // apply column group states from favorites
        const savedColumnGroupState = activeFavorite.jsonBlob.columnGroupState;
        if (savedColumnGroupState && savedColumnGroupState.length > 0 && this.gridApi) {
          this.gridApi.setColumnGroupState(savedColumnGroupState);
        }
      }
    }
    // after refresh, when attributes have changed, turn loading state to false
    if (
      this.state.isTopAttributesRefreshing &&
      prevProps.topAttributesData !== this.props.topAttributesData &&
      !isEmpty(this.props.topAttributesData)
    ) {
      this.setState({ isTopAttributesRefreshing: false });
    }
  }

  componentWillUnmount() {
    this.isUnmounting = true;
    if (this.gridApi) {
      this.gridApi.removeEventListener('columnGroupOpened', this.onColumnGroupOpened);
    }
  }

  onUpdateConfig = (config: any) => {
    const api = this.gridApi;
    if (config.isDefault) {
      this.props.updateCompanionSortField(this.props.defaultCompanionSortField ?? '');
      this.props.updateCompanionSortDirection('desc');
      // Update the columns to make sure they stay in sync when applying 'Reset'
      if (api) {
        config.columns.forEach((viewItem: any) => {
          const colId = viewItem.dataIndex;
          if (viewItem.pinned === 'left') {
            api.setColumnsPinned([colId], 'left');
          } else {
            api.setColumnsPinned([colId], null);
          }
        });
        // reset column group state to default so changes from favorites dont 'stick'
        api.resetColumnGroupState();
      }
    }

    // use groupedCols if they are there
    const nested = config.keyGroupedColumns ?? config.columns;
    const nestedCopy = cloneDeep(nested);

    // now do a full-depth flatten for the grid
    const flat = flattenConfigItems(nestedCopy);

    // Reset the new pinned states manually to ensure that the cols update immediately after viewConfig changes
    // NOTE: This was added for INT-4676 - default pinned cols were not unpinning
    if (api && !config.isDefault) {
      flat.forEach((viewItem: any) => {
        const colId = viewItem.id || viewItem.dataIndex;
        if (viewItem.pinned === 'left') {
          api.setColumnsPinned([colId], 'left');
        } else {
          api.setColumnsPinned([colId], null);
        }
      });
    }

    // build a new config object so nothing ever mutates in place (causes cols to render when they shouldn't after re-apply)
    const nextConfig: any = {
      ...config,
      isDefault: false,
      columns: flat,
      // only snapshot grouping on non‐default views:
      ...(config.isDefault ? {} : { keyGroupedColumns: nestedCopy }),
    };

    this.props.onUpdateConfig(nextConfig);
  };

  onFabClick = () => {
    switch (this.props.fab.fabType) {
      case FabType.cart:
        if (isNil(this.props.assortmentCartLink)) {
          return;
        }
        this.props.addSelectedItemsToCart();
        this.props.routerNavigate(this.props.assortmentCartLink);
        break;
      case FabType.planning:
        this.props.updateAssortmentPlan();
        break;
      default:
        break;
    }
  };

  /**
   * Used to trigger redux refreshes, mainly for `topAttributes` editing
   */
  maybeRefreshAfterEdit = (
    shouldRefresh?: ConfigurableGridColumnDef['shouldRefresh'],
    externalRefresh?: PendingRedecorateActionPayload,
  ) => {
    if (this.props.topAttributesRefresh === zRefreshOn.enum.all) {
      // to set skeleton state in the macros section while top attributes are refreshing
      this.setState({ isTopAttributesRefreshing: true });
      this.props.onRefreshTopAttributes();
    }
    if (shouldRefresh === 'full') {
      this.props.onRefreshConfigurableGridData();
    }
    // Tell external views (for example an open `HistoryStylePane`) that these rows changed so they
    // can redecorate to stay consistent with the grid. The `configurableGrid` sourceComponent keeps
    // this grid from redecorating itself off its own edit (see `isNotSameExternalDataRefreshRequest`).
    if (externalRefresh != null) {
      this.props.requestExternalDataRefresh(externalRefresh);
    }
  };

  generateCompanionViewData = (data: BasicPivotItem[]): ListViewable[] => {
    const { idProp, unmodifiedViewDefn } = this.props;
    if (isNil(unmodifiedViewDefn)) return [];
    const { companionGrid } = unmodifiedViewDefn;
    const companionSortField = (this.props.companionSortField ?? this.props.defaultCompanionSortField) || '';
    const companionSortDirection = this.props.companionSortDirection;
    const removedGroupData = data.filter((dat) => {
      // Groups only have one attribute and one meta property in the object: group and id and gridasyncstate
      return Object.keys(dat).length > 3;
    });

    const sortedData = simpleByField(removedGroupData, companionSortField, companionSortDirection);

    const compData: ListViewable[] = uniqBy(
      sortedData.map((d) => {
        if (!isNil(companionGrid.main)) {
          // if the config exists, use those properties
          const { title, image, body, stars, displayTitle } = companionGrid.main; // only stars is optional
          // resolve keys/classes safely with new styling capabilities
          const titleKey = getCompanionDataIndex(title as StyledConfigValue);
          const bodyKey = getCompanionDataIndex(body as StyledConfigValue);
          const displayTitleKey = displayTitle ? getCompanionDataIndex(displayTitle as StyledConfigValue) : undefined;

          // classes from stylable fields
          const titleClasses = displayTitle
            ? getCompanionClasses(displayTitle as StyledConfigValue)
            : getCompanionClasses(title as StyledConfigValue);
          const bodyClasses = getCompanionClasses(body as StyledConfigValue);

          // for UI title text: displayTitle wins, else title
          const shownTitleKey = displayTitleKey ?? titleKey;

          return {
            id: titleKey ? d[titleKey] : '',
            title: shownTitleKey ? d[shownTitleKey] : '',
            name: bodyKey ? d[bodyKey] : '',

            'member:style:id': d['member:style:id'],
            'member:style:name': d['member:style:name'],
            'member:stylecolor:id': d['member:stylecolor:id'],
            'member:stylecolor:name': d['member:stylecolor:name'],
            stars: stars ? parseInt(d[stars], 10) : null,
            imageUri: !isNil(image) ? d[image] : null,
            fieldClasses: {
              title: titleClasses,
              name: bodyClasses,
            },
          };
        } else {
          // use hardcodes values
          const id = d[`member:${idProp}:id`] ? d[`member:${idProp}:id`] : d[idProp];
          const name = d[`member:${idProp}:name`] ? d[`member:${idProp}:name`] : d.name;
          const description = d[`member:${idProp}:description`] ? d[`member:${idProp}:description`] : d.description;
          return {
            id: id,
            title: name,
            name: description,
            'member:style:id': d['member:style:id'],
            'member:style:name': d['member:style:name'],
            'member:stylecolor:id': d['member:stylecolor:id'],
            'member:stylecolor:name': d['member:stylecolor:name'],
            stars: d['compositeattributeband'] ? parseInt(d['compositeattributeband'], 10) : 0,
            imageUri: d['attribute:img:id'],
          };
        }
      }),
      'id',
    );

    return compData;
  };

  renderCompanionView = (data: BasicPivotItem[]) => {
    const { idProp, defaultCompanionSortField, isCompanionCollapsed, onToggleCompanion } = this.props;
    const { companionScrollTo } = this.state;
    const compData = this.generateCompanionViewData(data);
    const companionSortField = (this.props.companionSortField ?? defaultCompanionSortField) || '';

    if (compData) {
      const companionProps: CompanionListProps = {
        config: {
          sortBy: {
            options: this.props.companionSortOptions,
            defaults: { dataIndex: defaultCompanionSortField || '' },
          },
          itemMappings: {} as CompanionView['itemMappings'],
        },
        selectedItemId: this.state.selectedId ?? null,
        sortField: companionSortField,
        data: compData,
        isDataloaded: this.props.dataLoaded,
        dataLookup: undefined as unknown as CompanionDataLookup,
        scrollTo: companionScrollTo,
        sortDirection: this.props.companionSortDirection,
        isCompanionCollapsed,
        renderSearchComponent: false,
        renderFilterComponent: false,
        onItemSelect: (identityValue: string) => {
          // TODO: make this logic reusable so it can be invoked during handlePendingCellUpdate if needed
          function findIndexComp(dataKey: string) {
            return compData.findIndex((datum) => {
              const fieldFound = datum[dataKey] ? datum[dataKey] : datum.name;
              return fieldFound === identityValue;
            });
          }
          let key = idProp === 'id' ? 'id' : `member:${idProp}:id`;
          let index = findIndexComp(key);

          if (index === -1) {
            key = idProp === 'id' ? 'name' : `member:${idProp}:name`;
            index = findIndexComp(key);
          }

          const newState = {
            gridScrollTo: {
              eventId: Date.now(),
              where: {
                key: key,
                value: identityValue,
              },
            },
            selectedId: compData[index].id,
          };
          this.setState(newState);
        },
        onSortDirectionChange: (direction) => {
          this.setState({
            companionScrollTo: this.state.selectedId
              ? {
                  eventId: Date.now(),
                  where: {
                    key: 'id',
                    value: this.state.selectedId,
                  },
                }
              : undefined,
          });
          this.props.updateCompanionSortDirection(direction);
        },
        onSortChange: (sortByDataIndex) => {
          this.setState({
            companionScrollTo: this.state.selectedId
              ? {
                  eventId: Date.now(),
                  where: {
                    key: 'id',
                    value: this.state.selectedId,
                  },
                }
              : undefined,
          });
          this.props.updateCompanionSortField(sortByDataIndex);
        },
        onToggleCompanion,
      };
      return <CompanionList {...companionProps} />;
    } else {
      return <div />;
    }
  };

  handleChangeFloorsetDropdown = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { floorsetDropdownProps, updateTopMemberSelection } = this.props;
    if (isNil(floorsetDropdownProps)) {
      return;
    }

    const newValue = event.currentTarget.textContent;
    const valueIndex = findIndex(floorsetDropdownProps.options, (option) => {
      return option.text === newValue;
    });
    const selection = floorsetDropdownProps.options[valueIndex].id as string;
    updateTopMemberSelection(selection);
  };

  getActiveFloorset = (): string => {
    const { floorsetDropdownProps } = this.props;
    const selection = floorsetDropdownProps?.selection;

    if (isNil(floorsetDropdownProps)) {
      return '';
    }

    const saferSelection = selection && selection > -1 ? selection : 0;
    return get(floorsetDropdownProps.options, saferSelection).dataIndex;
  };

  getSelectedRowNodes = (): IRowNode[] => {
    if (this.gridApi == null) return [];
    const selectedNodes: IRowNode[] = this.gridApi.getSelectedNodes();
    const nodesAfterFilter: IRowNode[] = [];
    this.gridApi.forEachNodeAfterFilter((n) => {
      nodesAfterFilter.push(n);
    });

    // Filter the selected nodes based on column filters in the filter model
    return intersection(selectedNodes, nodesAfterFilter);
  };

  // This type is wrong I believe it's actually `IRowNode` but it doesn't make much difference
  getSelectedRows = (): PivotBasicItem[] => {
    const nodesIntersection = this.getSelectedRowNodes();

    const floorsetId = this.getActiveFloorset();
    return nodesIntersection
      .filter((n) => {
        return this.hasSelectableLevels() ? true : isNil(n.allChildrenCount) || n.allChildrenCount <= 0;
      })
      .map((n) => {
        // groupRows, (present when using `selectableLevels`) don't have `node.data`, so we change that to {},
        // if this breaks something somewhere, I'm sorry
        // Mark Todd
        const rowData = n.data ?? {};
        rowData.floorset = floorsetId;
        return rowData;
      });
  };

  /** `maybeGetFirstLeafNode` was added for a specific use of `noSelectListData` where we need to send _some_ data
   * to the pivot call (like `floorset`) so we need a place that can be adjusted to pull it from,
   * and the 1st pivot item is the best place for that. See INT-6000 for more info.
   */
  maybeGetFirstLeafNode = (): IRowNode | undefined => {
    if (this.gridApi == null) return;
    let firstRow: IRowNode | undefined = undefined;
    this.gridApi.forEachLeafNode((n) => {
      // loop and find the first row then stop looking
      if (!isNil(firstRow)) return;
      firstRow = n;
    });
    return firstRow;
  };

  submitGlobalUpdate = async ({ dataIndex, value }: Omit<MassColumnUpdateParams, 'value'> & { value: unknown }) => {
    // This fun mess removes that silly wrapped colon stuff (eg: attribute:<x>:id)
    // This is the more concise version: .replace(/(?:^.*?:)?([^:]*)(?::.*)?/, '$1')
    const key = dataIndex.replace(/(member|attribute):/, '').replace(/:(id|name|description)/, '');
    // TODO: may need to devise a more configurable way for setting up the coordinateMap
    // when not reliant on specific view items
    const coordinates = [
      {
        product: this.props.scopeSelection.productMember || '',
        location: this.props.scopeSelection.locationMember || '',
      },
    ];
    const payload: CoarseEditPayload = {
      coordinates,
      [dataIndex]: value,
    };

    let calcValue = value;
    if (isBoolean(value)) {
      calcValue = value ? 'true' : '';
    }
    payload[key] = calcValue;
    return await ServiceContainer.pivotService.coarseEditSubmitData(payload);
    // await fetch new value set & merge
  };

  publishAssortment = async (actionType?: 'publish' | 'unpublish' | 'link') => {
    if (!actionType) return;
    const selectedItems = this.getSelectedRows();

    const publishAttribute = this.props.publishAttribute || IS_PUBLISHED;
    if (isEmpty(selectedItems)) {
      return;
    }
    let coordMap: {
      [s: string]: string;
    };
    if (this.props.updateCoordinateMap != null) {
      coordMap = this.props.updateCoordinateMap;
    } else if (this.props.massEditConfig != null) {
      coordMap = this.props.massEditConfig.coordinateMap;
    } else {
      logError(`Cannot update dc_publish. Somehow set to generic update without updateCoordinateMap property.`, null);
      return;
    }
    const coordinates = selectedItems.map((rowData) =>
      omitBy(
        mapValues(coordMap, (v) => {
          const value = rowData[v];
          return value;
        }),
        isNil,
      ),
    );

    if (this.props.submitPayload) {
      const payload = {
        coordinates,
        [publishAttribute]: actionType === 'publish' ? 1 : 0,
      };
      await this.props.submitPayload(payload).then(() => {
        this.setState({
          isConfirmationModalOpen: false,
        });
        this.gridApi?.deselectAll();
      });
      // refresh data to see update
      this.props.onRefreshConfigurableGridData();
    }
  };

  renderConfirmationModal = () => {
    if (this.state.actionType === 'link') {
      // this will break if there is more than one link
      const linkButtonConfig = find(this.props.actions?.buttons, { type: 'link' }) as
        | ConfigurableGridLinkButton
        | undefined;
      if (!linkButtonConfig) {
        ServiceContainer.loggingService.error('Link button configured, but no config found');
        return;
      }
      return (
        <LinkConfirmationModal
          isOpen={this.state.isConfirmationModalOpen}
          descriptionText={
            linkButtonConfig.type === 'link' && linkButtonConfig.params.description
              ? linkButtonConfig.params.description
              : (linkButtonConfig.tooltip ?? 'Complete Action')
          }
          dataApi={linkButtonConfig.params.dataApi}
          breadcrumb={linkButtonConfig.params.breadcrumb}
          getSelectedItems={() => this.getSelectedRows()}
          onAfterSuccess={() => {
            this.gridApi?.deselectAll();
          }}
          onClose={() => {
            this.gridApi?.deselectAll();
            this.setState({ isConfirmationModalOpen: false });
          }}
        />
      );
    }
    return (
      <ConfirmationModal
        isLoading={this.state.isConfirmationModalLoading}
        isOpen={this.state.isConfirmationModalOpen}
        descriptionText={`Are you sure you wish to ${this.state.actionType}?`}
        onConfirm={async () => {
          this.setState({ isConfirmationModalLoading: true });
          try {
            await this.publishAssortment(this.state.actionType);
          } catch (e) {
            toast.error('Failed To Publish');
            ServiceContainer.loggingService.error('Failed To Publish');
          } finally {
            this.setState({ isConfirmationModalLoading: false });
          }
        }}
        onCancel={() => {
          this.gridApi?.deselectAll();
          this.setState({
            isConfirmationModalOpen: false,
          });
        }}
      />
    );
  };

  handleConfigurablePostAction = (updateType: 'coarse' | 'granular', value: unknown, dataIndex: string) => {
    // for now, assuming will always be department (not location) and coarse (not granular)?

    const update = {
      nodes: this.getSelectedRows() as unknown as BasicPivotItem[],
      dataIndex,
      value,
    };

    this.submitGlobalUpdate(update)
      .catch((error) => {
        toast.error(`${(error as any).message}`);
      })
      .finally(() => {
        this.props.onRefreshConfigurableGridData();
      });
  };

  generateSubheaderExtraDropdownProps(): SubheaderDropdownProps[] {
    const { floorsetDropdownProps } = this.props;
    let dropdowns: SubheaderDropdownProps[] = [];

    if (!isNil(floorsetDropdownProps)) {
      dropdowns = concat(dropdowns, {
        ...floorsetDropdownProps,
        dataQa: 'floor-sets',
        handleChangeOnDropdown: this.handleChangeFloorsetDropdown,
      });
    }
    return dropdowns;
  }

  generateSubheaderExtraActionButtonProps(): SubheaderActionButtonProps {
    const { dataLoaded, massEditConfig, configurablePostAction } = this.props;

    const massEdit = isNil(massEditConfig)
      ? undefined
      : {
          config: massEditConfig,
          title: massEditConfig.title,
          handleSubmit: () => {
            this.gridApi?.deselectAll();
            this.props.onRefreshConfigurableGridData();
          },
          getSelectedItems: () => this.getSelectedRows(),
          handleCancel: () => this.gridApi?.deselectAll(),
          gridApi: this.gridApi,
          dataLoading: this.state.gridRdyCnt === 0,
        };

    const postAction = isEmpty(configurablePostAction)
      ? undefined
      : {
          action: formatConfigurableActionItem(configurablePostAction, this.handleConfigurablePostAction),
          dataLoading: !dataLoaded,
        };

    return {
      massEdit,
      postAction,
    };
  }

  onGraphItemClick = (data: unknown) => {
    const { configuratorViewDefn, floorsetDropdownProps } = this.props;
    if (isNil(configuratorViewDefn) || isNil(configuratorViewDefn.graph)) return;
    const timeDropdownPresent = !isNil(floorsetDropdownProps);
    const ignoreClicks = configuratorViewDefn.graph.ignoreClicks;
    if (ignoreClicks || timeDropdownPresent) {
      return;
    }

    const dataIndex = configuratorViewDefn.graph.timeDataIndex;
    const nextTime: string = (data as { [key: string]: string })[dataIndex];
    this.props.updateTopMemberSelection(nextTime);
  };
  updateSelectedItem = (event: RowSelectedEvent) => {
    const { node } = event;
    if (node.parent && !isNil(node.parent.childrenAfterFilter)) {
      this.debouncedRenderUpdate();
    }
    this.refreshRowSelectability();
  };
  debouncedRenderUpdate = debounce(() => {
    this.forceUpdate();
  }, 100);

  handleItemClick = (selections: BasicPivotItem | BasicPivotItem[]) => {
    const canAddToCart = !isNil(this.props.cartItemType);
    if (canAddToCart) {
      this.props.toggleSelections(selections);
    }
  };

  handlePopoverItemClick = (item: BasicPivotItem) => {
    // pass through to popover HOC to handle
    if (this.props.onItemClicked) {
      this.props.onItemClicked(item);
    }
  };

  /** This function makes several assumptions about the shape of the data that it needs to create it's scopes.
   * This is due to the unique nature of this configuration.
   * Notably it presumes grouped columns with children that have real data in them, which it requires in order to map
   * to mfp scope anchors.
   */
  createMfpScope = (button: ConfigurableGridButton) => {
    const api = this.gridApi;
    if (!api) {
      logWarn('GridApi is not present');
      return;
    }
    const selectedItems = api.getSelectedNodes();
    if (button.type !== 'multiSelectCreateMfpScope') {
      return;
    }
    const coordMap = button.params.coordinateMap;
    const selectedItemsFirstChildren = selectedItems.flatMap((groupRow) => groupRow.allLeafChildren[0].data);

    const coordinates = mapValues(coordMap, (v) => {
      return selectedItemsFirstChildren.map((i) => i[v]);
    });
    this.setState(
      {
        isMfpScopeLoading: true,
      },
      () => {
        // FIXME get this type right
        this.props
          // @ts-ignore
          .createMfpScope(coordinates, button.params.coordinateMap.workflow)
          .then(() => {
            if (this.props.setPreviousView) {
              this.props.setPreviousView(this.props.routerLocation.pathname);
            }
            this.props.routerNavigate(button.params.path);
          })
          .catch((_e) => {
            toast.error(`Failed To ${button.text}`);
            ServiceContainer.loggingService.error(`Failed To ${button.text}`);
          })
          .finally(() => {
            if (!this.isUnmounting) {
              this.setState({ isMfpScopeLoading: false });
            }
          });
      },
    );
  };

  /** True if this row hit the buttons maxSelections limit, so its checkbox should show disabled
   * instead of hidden - false if maxSelections isnt configured. */
  isMaxSelectionsBlocked = (row: IRowNode): boolean => {
    const agGroupingField = row.field || '';
    const mfpScopeButton = this.props.actions?.buttons?.find((btn) => {
      return btn.type === 'multiSelectCreateMfpScope' && !!btn.selectableLevels?.includes(agGroupingField);
    });
    const maxSelections =
      mfpScopeButton?.type === 'multiSelectCreateMfpScope' ? mfpScopeButton.params.maxSelections : undefined;
    if (isNil(maxSelections) || maxSelections <= 0 || row.isSelected()) return false;
    const selectedCount = this.gridApi?.getSelectedNodes().filter((n) => n.field === agGroupingField).length ?? 0;
    return selectedCount >= maxSelections;
  };

  handleIsRowSelectable = (row: IRowNode): boolean => {
    const selectableLevels = this.props.actions?.buttons
      ?.flatMap((btn) => {
        return btn.selectableLevels;
      })
      .filter((sl) => !isNil(sl));
    if (this.shouldEnableCheckboxSelection() && isEmpty(selectableLevels)) return true;
    // FIXME this is bodged in for now and I don't prefer it actually work like this
    const agGroupingField = row.field || '';
    // not a checkable row at all
    if (!selectableLevels?.includes(agGroupingField)) return false;
    // checkable, but blocked if it hit the maxSelections limit - always false (no effect) if
    // maxSelections isnt configured, so this matches old behavior for existing usages
    return !this.isMaxSelectionsBlocked(row);
  };

  /** re-checks every rows selectable/disabled state after a selection change. ag-grid only recomputes
   * isRowSelectable when we hand it a new function reference, so we pass a fresh one each time
   * if maxSelections isnt configured, nothing happens */
  refreshRowSelectability = () => {
    const hasMaxSelections = this.props.actions?.buttons?.some(
      (btn) =>
        btn.type === 'multiSelectCreateMfpScope' && !isNil(btn.params.maxSelections) && btn.params.maxSelections > 0,
    );
    if (!hasMaxSelections || !this.gridApi) return;
    this.gridApi.setGridOption('isRowSelectable', (row: IRowNode) => this.handleIsRowSelectable(row));
    this.gridApi.refreshCells({ columns: [SELECTION_CHECKBOX_COL_ID], force: true });
  };

  createCustomActionModal = () => {
    const { actionPopupBtnConf } = this.state;
    const curAction = actionPopupBtnConf;

    switch (curAction?.type) {
      case 'setValuePost':
      case 'multiSelectPost':
      case 'noSelectListData':
      case 'withSelectListData':
      case 'singleSelectPost':
      case 'removeFromAssortment':
        return (
          <ActionModal
            onToggleModal={(togType) => {
              if (togType === 'apply') {
                this.props.onRefreshConfigurableGridData();
              }
              this.setState({
                actionPopupActive: false,
                actionPopupBtnConf: undefined,
              });
            }}
            isOpen={!!this.state.actionPopupActive}
            selectedItems={this.getSelectedRows()}
            config={curAction}
          />
        );
      default:
        return;
    }
  };

  shouldEnableCheckboxSelection = (): boolean => {
    const {
      massEditConfig,
      showPublish,
      cartItemType,
      actions,
      allowWorklistFunctionality = false,
      columnDefs,
    } = this.props;
    const haveHeaditors = columnDefs.some((cd) => cd.useColumnHeaderMassEditUpdate === true);
    return (
      !isNil(massEditConfig) ||
      showPublish ||
      !isNil(cartItemType) ||
      !isEmpty(actions?.buttons?.filter((b) => !isNoSelectButtontype(b))) ||
      allowWorklistFunctionality ||
      haveHeaditors
    );
  };

  /** Checks whether any `viewConfig.actions.buttons` has the `selectableLevels` property, which changes how row selection works */
  hasSelectableLevels = (): boolean => {
    return !isEmpty(
      this.props.actions?.buttons
        ?.flatMap((btn) => {
          return btn.selectableLevels;
        })
        .filter((sl) => !isNil(sl)),
    );
  };

  //#region SplitButton Menu Item Functions

  getPublishMenuItems = (): MenuItemDef[] => {
    const { publishText, showPublish, hideUnpublish = false, unpublishText } = this.props;
    const publishMenuItems: MenuItemDef[] = [];

    if (showPublish) {
      const menuText = publishText ?? 'Publish';
      publishMenuItems.push({
        name: menuText,
        action: () => {
          this.setState({
            actionType: 'publish',
            isConfirmationModalOpen: true,
          });
        },
        icon: 'fal fa-plus-circle',
        tooltip: `${menuText} selected items`,
      });

      if (!hideUnpublish) {
        const unpubMenuText = unpublishText ?? 'Unpublish';
        publishMenuItems.push({
          name: unpubMenuText,
          action: () => {
            this.setState({
              actionType: 'unpublish',
              isConfirmationModalOpen: true,
            });
          },
          icon: 'fal fa-minus-circle',
          tooltip: `${unpubMenuText} selected items`,
        });
      }
    }

    return publishMenuItems;
  };

  // helpers for worklist link options
  openLinkModal = () => this.setState({ isLinkModalOpen: true });
  closeLinkModal = () => this.setState({ isLinkModalOpen: false });

  getWorklistMenuItems = (): MenuItemDef[] => {
    const { allowWorklistFunctionality = false, idProp, worklistConfigButtons } = this.props;
    let worklistMenuItems: MenuItemDef[] = [];
    const api = this.gridApi;
    if (allowWorklistFunctionality && worklistConfigButtons && api) {
      worklistMenuItems = concat(
        worklistMenuItems,
        getWorklistMenuItems('multiselect', {
          identityField: idProp,
          getGridApi: () => api,
          actions: { onPostAction: () => this.gridApi?.deselectAll() },
          worklistConfigButtons,
          onLinkClick: this.openLinkModal,
        }) as MenuItemDef[],
      );
    }

    return worklistMenuItems;
  };
  buttonTypeToCallback = (type: ConfigurableGridButton['type'], btnConf: ConfigurableGridButton): (() => void) => {
    switch (type) {
      case 'multiSelectCreateMfpScope':
        // TODO this needs to roll into a loading state somehow

        return () => {
          this.createMfpScope(btnConf);
        };
      case 'link':
        // the 'link' renderer here behaves both link a custom action button and like a publish button
        // this is to re-use the publish confirmation modal
        return () => {
          this.setState({
            actionType: 'link',
            isConfirmationModalOpen: true,
          });
        };
      case 'withSelectListData':
        return () => {
          const selectedItems = this.getSelectedRows();
          if (!isListDataButton(btnConf)) {
            // this is just here for typescript sake, the closure means it doesn't know the btnConf is still the
            // same type as above.
            return;
          }
          if (isEmpty(selectedItems)) {
            // this is safety valve, button should be disabled when empty
            return () => {};
          }
          const alteredConf = cloneDeep(btnConf);

          const selectedItemApis = selectedItems.map((s) => {
            return processApiParams(btnConf.params.dataApi, s);
          });
          const mergedApi = selectedItemApis.reduce((accumApi, currentApi) => {
            const merged = accumApi.params;
            const current = currentApi.params;
            Object.keys(current).forEach((key) => {
              // we ignore non-arrays since those are constants (processApiParams makes arrays
              // when it's populating variables)
              if (!isArray(merged[key] || !isArray(current[key]))) {
                return;
              }
              if (merged[key] != null) {
                merged[key] = union(merged[key], current[key]);
              } else {
                merged[key] = current[key];
              }
            });
            return accumApi;
          });
          mergedApi.params = mapValues(mergedApi?.params, (v) => {
            return isArray(v) ? v.join(',') : v;
          });
          alteredConf.params.dataApi = mergedApi;
          this.setState({
            actionPopupActive: true,
            actionPopupBtnConf: alteredConf,
          });
        };
      case 'noSelectListData':
        return () => {
          if (!isListDataButton(btnConf)) {
            // this is just here for typescript sake, the closure means it doesn't know the btnConf is still the
            // same type as above.
            return;
          }
          const alteredConf = cloneDeep(btnConf);
          alteredConf.params.dataApi.params = processApiParams(
            alteredConf.params.dataApi,
            this.maybeGetFirstLeafNode(),
          ).params;
          this.setState({
            actionPopupActive: true,
            actionPopupBtnConf: alteredConf,
          });
        };
      case 'setValuePost':
      case 'singleSelectPost':
      case 'removeFromAssortment':
      case 'multiSelectPost':
        return () => {
          this.setState({
            actionPopupActive: true,
            actionPopupBtnConf: btnConf,
          });
        };
      default:
        return () => {
          ServiceContainer.loggingService.warn(
            `Missing callback for action button type: ${type} on screen ${window.location.href}`,
          );
        };
    }
  };
  getCustomNoSelectMenuItems = (): MenuItemDef[] => {
    const { actions } = this.props;
    const noSelectButtons = actions?.buttons?.filter((b) => isNoSelectButtontype(b)) ?? [];
    return noSelectButtons.map((btn) => {
      return {
        name: btn.text,
        tooltip: btn.tooltip ?? btn.text,
        action: this.buttonTypeToCallback(btn.type, btn),
        icon: iconClassToString(`${btn.icon ?? 'fal fa-bars'}`),
      };
    });
  };

  getCustomActionMenuItems = (): MenuItemDef[] => {
    const { actions } = this.props;
    const buttons = actions?.buttons?.filter((b) => !isNoSelectButtontype(b)) ?? [];

    return buttons.map((btn) => {
      return {
        name: btn.text,
        tooltip: btn.tooltip ?? btn.text,
        action: this.buttonTypeToCallback(btn.type, btn),
        icon: iconClassToString(`${btn.icon ?? 'fal fa-bars'}`),
      };
    });
  };

  getSplitButtonMenuItems = (): MenuItemDef[] => {
    const worklistMenuItems = this.getWorklistMenuItems();
    const publishMenuItems = this.getPublishMenuItems();
    const customActionMenuItems = this.getCustomActionMenuItems();
    return concat(worklistMenuItems, publishMenuItems, customActionMenuItems);
  };

  generateDownloadLinkContext = () => {
    const maybeTopMembersToAdd: string[] = [];
    const maybeTopMember = { topMember: '' };

    if (this.props.topMembers) {
      maybeTopMembersToAdd.push(this.props.topMembers);
    }
    if (this.getActiveFloorset()) {
      maybeTopMembersToAdd.push(this.getActiveFloorset());
    }
    maybeTopMember.topMember = maybeTopMembersToAdd.join(',');

    return {
      ...maybeTopMember,
      ...this.props.topMemberObj,
    };
  };
  //#endregion

  render() {
    const {
      title,
      viewDefnState,
      dataLoaded,
      showFlowStatus,
      massEditConfig,
      showPublish,
      configuratorViewDefn,
      unmodifiedViewDefn,
      defaultCompanionSortField,
      groupByDropdownProps,
      topAttributesData,
      topAttributesDataState,
      data,
      leafIdProp,
      idProp,
      dependentCalcs,
      dataApi,
      adornments,
      salesAdjustmentConfig,
      clientActionHandlers,
      updateCoordinateMap,
      onlySendCoordinateMap,
      groupBySelection,
      configureSelections,
      topMembers,
      topMemberObj,
      isWorklistActive,
      showLookBackPeriod,
      sideNavCollapsed,
      syncGridData,
      removeCrumbsAfterId,
      removeLastCrumb,
      resetCrumbsByKey,
      resetAllCrumbs,
      clearActiveRestore,
    } = this.props;

    if (!isViewDefnLoaded(viewDefnState)) {
      return <Overlay type="loading" visible={true} />;
    }

    const { gridScrollTo, activeStyleColor, isTopAttributesRefreshing } = this.state;

    const viewConfigurator: ViewConfiguratorModalProps | undefined = this.onUpdateConfig &&
      configuratorViewDefn &&
      // FIXME The type for ViewConfiguratorModal is all sorts of messed up and is hidden by the fact
      // that previously the viewdefn objects were any https://s5stratosdev.atlassian.net/browse/INT-5916
      (unmodifiedViewDefn as any) && {
        viewConfig: configuratorViewDefn,
        // configurationSelections: this.props.configureSelections,
        configureSelections: this.props.configureSelections,
        unmodifiedViewDefn: unmodifiedViewDefn,
        updateConfig: this.onUpdateConfig,
        showPinCheckboxForGrid: true,
        getGridApi: () => {
          if (this.gridApi) {
            return this.gridApi;
          }
          return;
        },
        companionData: {
          companionSortDirection: this.props.companionSortDirection,
          companionSortField: this.props.companionSortField,
        },
        defaultCompanionData: {
          companionSortDirection: 'desc',
          companionSortField: defaultCompanionSortField,
        },
      };

    const subheaderProps: SubheaderOwnProps = {
      title: title || '',
      showFlowStatus,
      showLookBackPeriod,
      summary: '',
      showSearch: true,
      // Override group by check since view does not have a separate groupByDefn file
      showGroupBy: this.props.unmodifiedViewDefn?.subheaderDropdowns?.groupBy != null,
      extraDropdowns: this.generateSubheaderExtraDropdownProps(),
      extraActionButtons: this.generateSubheaderExtraActionButtonProps(),
      downloadLink: this.props.subheader?.downloadLink,
      downloadLinkContext: this.generateDownloadLinkContext(),
      errorCondition: this.props.subheaderErrorText,
      viewConfigurator,
      favoritesSaveOverride: {
        groupBySelection: groupByDropdownProps?.selection as number | undefined,
        columnGroupState: this.state.columnGroupState,
      },
      viewDataState: this.props.viewDataState,
      notFetchGroupByViewDefn: !!groupByDropdownProps,
    };
    let configureModalProps: ConfigureModalProps;
    if (!isNil(this.props.configureOptions)) {
      const { configureIsOpen } = this.state;
      subheaderProps.configureOptions = {
        type: 'enabled',
        onConfigureClick: () => {
          this.setState({
            configureIsOpen: true,
          });
        },
      };
      const configureOptions = this.props.configureOptions;
      const updateSelections = this.props.updateConfigureSelections || noop;

      // Wire up configureSelections to the viewConfigurator, so they get saved in favorites
      if (viewConfigurator) {
        viewConfigurator.configureSelections = configureSelections;
        viewConfigurator.defaultConfigureSelections = configureOptions.defaultSelections;
        viewConfigurator.updateConfiguration = updateSelections;
      }

      configureModalProps = {
        enabled: true,
        isOpen: !!configureIsOpen,
        ...configureOptions,
        selections:
          this.state.currentConfigureSelections || this.props.configureSelections || configureOptions.defaultSelections,
        onReset: () => {
          this.setState({
            currentConfigureSelections: configureOptions.defaultSelections || [],
          });
        },
        onToggleModal: (action) => {
          // this.updateConfigureSelections(configureLastSelected);
          const nextState: Pick<ConfigurableGridState, 'configureIsOpen' | 'currentConfigureSelections'> = {
            configureIsOpen: false,
          };
          switch (action) {
            case 'apply': {
              if (isNil(this.state.currentConfigureSelections)) {
                ServiceContainer.loggingService.warn('User applied configure selections but none were found');
                return;
              }
              updateSelections(this.state.currentConfigureSelections);
              break;
            }
            case 'close': {
              this.setState({
                configureIsOpen: false,
              });
              return;
            }
            default: {
              nextState.currentConfigureSelections = this.props.configureSelections;
            }
          }
          this.setState(nextState);
        },
        selectionUpdate: (selections: Option[]) => {
          this.setState({
            currentConfigureSelections: selections,
          });
        },
      };
    } else {
      configureModalProps = { enabled: false };
    }

    const activeFloorset = this.getActiveFloorset();
    // this gets cloned because the incoming data is a frozen object from immer
    // and can't be extended
    const clonedData = cloneDeep(data);
    if (clonedData && clonedData[0] && !isEmpty(activeFloorset)) {
      set(clonedData[0], 'floorset', activeFloorset);
    }
    const editableGridOptions: EditableGridProps = {
      title: title,
      data: clonedData,
      // Props related to subheader filtering
      search: this.props.search,
      flowStatus: this.props.flowStatus,
      lookBackPeriod: this.props.lookBackPeriod,
      // end props subheader filtering
      columnDefs: this.props.columnDefs,
      groupColumn: this.props.groupColumn,
      gridScrollTo: isNil(this.props.activeBreadcrumbRestore?.selectedGridRowId)
        ? gridScrollTo
        : {
            eventId: Date.now(),
            where: {
              key: this.props.leafIdProp ?? this.props.idProp,
              value: this.props.activeBreadcrumbRestore?.selectedGridRowId,
            },
          },
      gridRowHeight: this.props.gridRowHeight,
      gridGroupByConfig: this.props.unmodifiedViewDefn?.main?.gridGroupByConfig,
      groupingKeyParams: this.props.unmodifiedViewDefn?.groupingKeyParams,
      groupRowHeight: this.props.groupRowHeight,
      refreshAfterEdit: this.maybeRefreshAfterEdit,
      handleGridReady: (params: GridReadyEvent) => {
        this.gridApi = params.api;
        this.setState({ gridRdyCnt: this.state.gridRdyCnt + 1 });
        this.gridApi.addEventListener('columnGroupOpened', this.onColumnGroupOpened);
      },
      doesRowPassFilter: this.props.doesRowPassFilter,
      onCellClicked: (event: CellClickedEvent) => {
        const { idProp } = this.props;
        if (event && event.data && !event.data.$$GroupHeader) {
          let key = idProp === 'id' ? 'stylecolor' : `member:${idProp}:id`;
          const identityValue = event.data[key];
          const companionData = this.generateCompanionViewData(this.props.data);

          const index = companionData
            ? companionData.findIndex((datum: ListViewable) => {
                const fieldFound = datum[key] ? datum[key] : datum.name;
                // Check if the field is present as a member
                if (datum[`member:${key}:id`] == identityValue) {
                  key = `member:${key}:id`;
                  return true;
                }
                return fieldFound === identityValue;
              })
            : event.data[key];

          if (index >= 0) {
            this.setState({
              companionScrollTo: {
                eventId: Date.now(),
                where: {
                  key: key,
                  value: identityValue,
                },
              },
              selectedId: companionData[index].id,
              activeStyleColor: event.data['stylecolor'],
            });
          }
        }
      },
      activeFloorset: this.getActiveFloorset(),
      // drilled props
      redecorateMap: this.props.configuratorViewDefn?.redecorateMap,
      redecorationOptions: this.props.unmodifiedViewDefn?.redecorationOptions,
      validValuesCache: this.state.validValuesCache,
      leafIdProp,
      idProp,
      pendingRedecorate: this.props.pendingRedecorate,
      topMembers,
      topMemberObj,
      configLoaded: isViewDefnLoaded(viewDefnState),
      activeStyleColor,
      dataLoaded,
      dependentCalcs,
      dataApi,
      adornments,
      salesAdjustmentConfig,
      showPublish,
      clientActionHandlers,
      massEditConfig,
      updateCoordinateMap,
      onlySendCoordinateMap,
      groupByDropdownProps,
      groupBySelection,
      configureSelections,
      addCrumb: (payload: AddBreadcrumbPayload) => {
        // insert necessary companion state into restore of breadcrumb
        const modifiedPayload = payload;
        set(modifiedPayload, 'currentCrumbPath.restore.selectedLevel', this.props.selectedLevel?.dataIndex ?? null);
        this.props.addCrumb(modifiedPayload);
      },
      removeCrumbsAfterId,
      removeLastCrumb,
      resetAllCrumbs,
      resetCrumbsByKey,
      clearActiveRestore,
      // the last check of these may not be always correct.  If we have an mfpCreateScope button then we need it without all the other options,
      // but usually one of the other checks covers the other multi select option
      enableCheckboxSelection: this.shouldEnableCheckboxSelection(),
      onItemClicked: this.handleItemClick,
      onPopoverItemClicked: this.handlePopoverItemClick,
      onRowSelected: this.updateSelectedItem,
      isRowSelectable: this.handleIsRowSelectable,
      isCheckboxDisabledButVisible: this.isMaxSelectionsBlocked,
      syncGridData,
      noRowsOverlayConfig: this.props.configuratorViewDefn?.noRowsOverlay,
      routerNavigate: this.props.routerNavigate,
      routerLocation: this.props.routerLocation,
      onFirstDataRendered: (event) => {
        // On page refresh, restore saved column group state here rather than relying solely
        // on componentDidUpdate, because this fires after columns and data are both ready
        const activeFavorite = this.props.favoritesList.find((x) => x.active === true);
        const savedColumnGroupState = activeFavorite?.jsonBlob?.columnGroupState;
        if (savedColumnGroupState && savedColumnGroupState.length > 0) {
          event.api.setColumnGroupState(savedColumnGroupState);
        }

        const breadcrumbRestore = this.props.activeBreadcrumbRestore;

        if (isNil(breadcrumbRestore)) {
          return;
        }

        // attempt to horizontally scroll grid to correct column
        if (!isNil(breadcrumbRestore.selectedGridColumnId)) {
          event.api.ensureColumnVisible(breadcrumbRestore.selectedGridColumnId, 'middle');
        }

        // attempt to vertically scroll grid to correct node
        if (!isNil(breadcrumbRestore.selectedGridRowId)) {
          const nextScrollTo = {
            eventId: Date.now(),
            where: {
              key: this.props.leafIdProp ?? this.props.idProp,
              value: breadcrumbRestore.selectedGridRowId,
            },
          };

          const node = event.api.getRowNode(breadcrumbRestore.selectedGridRowId);
          const nodeIndex = node?.rowIndex ?? 0;
          event.api.ensureIndexVisible(nodeIndex);

          // keep component state in sync with ag-grid changes
          this.setState({ gridScrollTo: nextScrollTo });
        }

        // Grid data is the last data loaded from restore for now, can safely delete the restore object.
        // Need to clear it so that navigating to another ConfigurableGrid view doesn't
        // try to restore state when breadcrumbs are no longer present/active.
        this.props.clearActiveRestore();
      },
    };

    // TODO: continue to abstract these extraRow components to their own components out of this file
    // to keep slimming this file down (linecount/logic)

    const splitButtonMenuItems = this.getSplitButtonMenuItems();
    const noDisableSplitButtonMenuItems = this.getCustomNoSelectMenuItems();
    const maybePublishText =
      this.props.showPublishText && !isEmpty(generateRollupText(topAttributesData, unmodifiedViewDefn));
    const selectedId = topMembers || this.state.selectedId || '';
    const topAttributesProps =
      topAttributesData && unmodifiedViewDefn && unmodifiedViewDefn.topAttributes
        ? generateTopAttributesProps(topAttributesData, unmodifiedViewDefn)
        : [];

    // moved condition up for showing skeleton
    const showMacroSkeleton =
      topAttributesDataState === ViewDataState.liveDataLoadingNoCache || isTopAttributesRefreshing;

    // account for if skeleton needs to be rendered in showMacro case
    // because on plan complete it was unmounting
    const showMacros = showMacroSkeleton || !isEmpty(topAttributesProps) || !!maybePublishText;

    const topExpanded = isNil(configuratorViewDefn?.topAttributes?.expanded)
      ? true
      : configuratorViewDefn.topAttributes.expanded;

    const selectedRowNodes = this.getSelectedRowNodes();
    const nowRowsSelected = isEmpty(selectedRowNodes);
    let rowsInvalid = false; // used only when `validateDataIndexes` is non-null

    // here we pull the button config to look for `validateDataIndexes`, and if present, validate the selected row's values against them
    const maybeButtonConfig = head(this.props.actions?.buttons);
    if (
      !nowRowsSelected &&
      maybeButtonConfig &&
      maybeButtonConfig.type === 'setValuePost' &&
      maybeButtonConfig.params?.disabledByDataIndexes &&
      !isEmpty(maybeButtonConfig.params?.disabledByDataIndexes)
    ) {
      rowsInvalid = maybeButtonConfig.params.disabledByDataIndexes.some((index) => {
        return selectedRowNodes.some((r) => {
          const rowValue = this.gridApi?.getValue(index, r);
          // we coerce the value to Truthy here.  The expectation is that `validateDataIndexes` will point at a truthy/falsy column,
          // and if any of the values in all of the selected rows `validateDataIndexes` are truthy, then the selection is invalid and the button will be disabled
          return !!rowValue;
        });
      });
    }

    const splitButtonActions = (
      <div>
        {!isEmpty(splitButtonMenuItems) && (
          <SplitButton
            text={splitButtonMenuItems[0].name}
            icon={splitButtonMenuItems[0].icon as string}
            isLoading={this.state.isMfpScopeLoading}
            isDisabled={nowRowsSelected || rowsInvalid}
            menuItems={splitButtonMenuItems.slice(1)}
            onButtonClick={splitButtonMenuItems[0].action ?? noop}
          />
        )}
      </div>
    );
    const noDisableSplitButtonActions = !isEmpty(noDisableSplitButtonMenuItems) ? (
      <div>
        <SplitButton
          text={noDisableSplitButtonMenuItems[0].name}
          icon={noDisableSplitButtonMenuItems[0].icon as string}
          isLoading={false}
          isDisabled={false}
          menuItems={noDisableSplitButtonMenuItems.slice(1)}
          onButtonClick={noDisableSplitButtonMenuItems[0].action ?? noop}
        />
      </div>
    ) : (
      <></>
    );
    const macroLoadingSkeleton = (
      <>
        {!isEmpty(unmodifiedViewDefn?.topAttributes?.left) && (
          <div className={macroStyles.rollupsContainer}>
            {(unmodifiedViewDefn?.topAttributes?.left as TopAttribute[]).map((_, index) => (
              <Skeleton key={`rollup-${index}`} variant="text" width={120} height={35} style={{ marginRight: 12 }} />
            ))}
          </div>
        )}
        {!isEmpty(unmodifiedViewDefn?.topAttributes?.right) && (
          <div className={macroStyles.calcTopAttributeStyle(sideNavCollapsed)}>
            {(unmodifiedViewDefn?.topAttributes?.right as TopAttribute[]).map((_, index) => (
              <Skeleton
                key={`attribute-${index}`}
                variant="rect"
                width={90}
                height={50}
                style={{ margin: '0 0.5rem' }}
              />
            ))}
          </div>
        )}
      </>
    );
    const splitBtnElement = (
      <div className={macroStyles.splitButtonContainer}>
        {noDisableSplitButtonActions}
        {splitButtonActions}
      </div>
    );
    const linkButton = this.props.worklistConfigButtons?.find((b) => b.type === 'link');

    return (
      <div className={listPairStyle}>
        <Subheader {...subheaderProps} />
        <div className="data-container">
          {this.props.hideCompanion === true ? null : this.renderCompanionView(data)}
          <div className="content-container">
            <div className={extraRowContainerStyles}>
              {!isNil(configuratorViewDefn?.graph) ? (
                <StyledAccordion
                  title={configuratorViewDefn.graph.title || 'Visualize'}
                  expanded={configuratorViewDefn.graph.expanded}
                >
                  <div style={{ minHeight: `${configuratorViewDefn.graph.height}px`, width: '100%' }}>
                    <MetricLineGraph
                      defnId={configuratorViewDefn.graph.configApi.params?.defnId || ''}
                      selectionRequired={isWorklistActive}
                      selectedId={selectedId}
                      height={configuratorViewDefn.graph.height}
                      timeDataIndex={configuratorViewDefn.graph.timeDataIndex}
                      onItemClick={this.onGraphItemClick}
                      onMouseOut={noop}
                      onMouseOver={noop}
                    />
                  </div>
                </StyledAccordion>
              ) : null}
              {showMacros && (
                <div className={macroStyles.macrosOuterContainer}>
                  <StyledAccordion
                    title={configuratorViewDefn?.topAttributes?.title || 'Macros'}
                    expanded={topExpanded}
                    rightTitleElement={splitBtnElement}
                  >
                    <div className={macroStyles.calcMacrosInnerContainerStyle(sideNavCollapsed)}>
                      {showMacroSkeleton ? (
                        macroLoadingSkeleton
                      ) : (
                        <>
                          {maybePublishText ? (
                            <Rollup config={unmodifiedViewDefn} topAttributes={topAttributesData} />
                          ) : null}
                          {!isEmpty(topAttributesProps) ? (
                            <TopAttributes topAttributes={topAttributesProps} sideNavCollapsed={sideNavCollapsed} />
                          ) : null}
                        </>
                      )}
                    </div>
                  </StyledAccordion>
                </div>
              )}
              {!showMacros && <div style={{ display: 'flex', justifyContent: 'flex-end' }}>{splitBtnElement}</div>}
              {linkButton?.type === 'link' && (
                <LinkConfirmationModal
                  isOpen={this.state.isLinkModalOpen}
                  descriptionText={
                    linkButton.type === 'link' && linkButton.params?.description
                      ? linkButton.params?.description
                      : (linkButton.tooltip ?? 'Complete Action')
                  }
                  dataApi={linkButton.params.dataApi}
                  breadcrumb={linkButton.params.breadcrumb}
                  getSelectedItems={this.getSelectedRows}
                  onClose={this.closeLinkModal}
                />
              )}
            </div>
            <div className={gridContainerStyle}>
              <EditableGrid {...editableGridOptions} />
            </div>
          </div>
        </div>

        {this.renderConfirmationModal()}
        <ConfigureModal {...configureModalProps} />
        {this.createCustomActionModal()}
      </div>
    );
  }
}
